// src/components/flight-data/ConcourseMap.js
import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { GATE_POSITIONS, WEST_PROPORTION, EAST_PROPORTION, TUNNELS } from './gatePositions';

/**
 * Component to display a visual map of the C-Concourse with active gates highlighted
 */
function ConcourseMap({ tdrData, activeGates = [], inboundGate, hotGates = [] }) {
  // Convert inbound gate to a number
  const inboundGateNum = parseInt(inboundGate?.replace(/\D/g, ''), 10);

  // Convert active gates to numbers for easier comparison
  const numericActiveGates = activeGates
    .map(gate =>
      typeof gate === 'string' ? parseInt(gate.replace(/\D/g, ''), 10) : gate
    )
    .filter(gate => !isNaN(gate));

  const getGateColor = (gateNum) => {
    // Priority 1: Inbound gate is always gold
    if (gateNum === inboundGateNum) {
      return { bg: '#F9B612', text: '#000' };
    }
    // Priority 2: Hot connection gates are red
    if (hotGates.includes(gateNum)) {
      return { bg: '#FF0000', text: '#fff' };
    }
    // Priority 3: Other active outbound gates are green
    if (numericActiveGates.includes(gateNum)) {
      return { bg: '#4CAF50', text: '#fff' };
    }
    // Default for inactive gates
    return { bg: '#333', text: '#aaa' };
  };

  const getGateStyle = (gateNum) => {
    const colors = getGateColor(gateNum);
    return {
      width: 28,
      height: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bg,
      color: colors.text,
      borderRadius: '4px',
      fontSize: '0.8rem',
      fontWeight: gateNum === inboundGateNum ? 'bold' : 'normal',
      border: 'none'
    };
  };

  // Group gates by wing and side
  const gatesByGroup = (wing, side) =>
    Object.keys(GATE_POSITIONS)
      .map(Number)
      .filter(g => GATE_POSITIONS[g].wing === wing && GATE_POSITIONS[g].side === side);

  const renderGateGroup = (wing, side) =>
    gatesByGroup(wing, side).map(gateNum => (
      <Box key={gateNum} sx={{ position: 'absolute', left: GATE_POSITIONS[gateNum].left }}>
        {/* Inactive gates carry a class so light themes can re-chrome them;
            category colors (inbound gold / hot red / outbound green) are
            insider language and stay inline, identical in every theme. */}
        <Box
          className={getGateColor(gateNum).bg === '#333'
            ? 'concourse-gate concourse-gate--inactive'
            : 'concourse-gate'}
          sx={getGateStyle(gateNum)}
        >
          {gateNum}
        </Box>
      </Box>
    ));

  return (
    <Paper
      className="concourse-map-card"
      sx={{
      p: 2,
      backgroundColor: 'rgba(30, 30, 30, 0.7)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      mb: 2
    }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '200px', overflow: 'hidden' }}>
        {/* North Side (Odd gates) */}
        <Box
          sx={{
            position: 'absolute',
            top: '40px',
            left: '0',
            width: '100%',
            display: 'flex',
          }}
        >
          {/* West wing */}
          <Box
            sx={{
              display: 'flex',
              width: `${WEST_PROPORTION}%`,
              paddingRight: '10px',
              position: 'relative'
            }}
          >
            {renderGateGroup('west', 'north')}
          </Box>

          {/* East wing */}
          <Box
            sx={{
              display: 'flex',
              width: `${EAST_PROPORTION}%`,
              paddingLeft: '10px',
              position: 'relative'
            }}
          >
            {renderGateGroup('east', 'north')}
          </Box>
        </Box>

        {/* Concourse Lines */}
        <Box
          className="concourse-line"
          sx={{
            position: 'absolute',
            top: '70px',
            left: '0',
            width: `${WEST_PROPORTION - 1}%`,
            height: '3px',
            backgroundColor: '#e2e2e2'
          }}
        />
        <Box
          className="concourse-line"
          sx={{
            position: 'absolute',
            top: '70px',
            right: '0',
            width: `${EAST_PROPORTION - 1}%`,
            height: '3px',
            backgroundColor: '#e2e2e2'
          }}
        />
        <Box
          className="concourse-line"
          sx={{
            position: 'absolute',
            top: '130px',
            left: '0',
            width: `${WEST_PROPORTION - 1}%`,
            height: '3px',
            backgroundColor: '#e2e2e2'
          }}
        />
        <Box
          className="concourse-line"
          sx={{
            position: 'absolute',
            top: '130px',
            right: '0',
            width: `${EAST_PROPORTION - 1}%`,
            height: '3px',
            backgroundColor: '#e2e2e2'
          }}
        />

        {/* South Side (Even gates) */}
        <Box
          sx={{
            position: 'absolute',
            top: '135px',
            left: '0',
            width: '100%',
            display: 'flex',
          }}
        >
          {/* West wing */}
          <Box
            sx={{
              display: 'flex',
              width: `${WEST_PROPORTION}%`,
              paddingRight: '10px',
              position: 'relative'
            }}
          >
            {renderGateGroup('west', 'south')}
          </Box>

          {/* East wing */}
          <Box
            sx={{
              display: 'flex',
              width: `${EAST_PROPORTION}%`,
              paddingLeft: '10px',
              position: 'relative'
            }}
          >
            {renderGateGroup('east', 'south')}
          </Box>
        </Box>

        {/* Tunnels / Breezeways */}
        {TUNNELS.map((tunnel, index) => {
          let leftPosition;
          if (tunnel.section === 'west') {
            const relativePos = (tunnel.position - 23) / (39 - 23);
            leftPosition = `calc(${relativePos * WEST_PROPORTION}%)`;
          } else {
            const relativePos = (tunnel.position - 40) / (67 - 40);
            leftPosition = `calc(${WEST_PROPORTION}% + ${relativePos * EAST_PROPORTION}%)`;
          }
          return (
            <Box
              key={`tunnel-${index}`}
              sx={{
                position: 'absolute',
                left: leftPosition,
                top: '70px',
                transform: 'translateX(-7px)',
              }}
            >
              <Box
                className="concourse-tunnel"
                sx={{
                  width: tunnel.label === 'Breezeway' ? 16 : 20,
                  height: 60,
                  backgroundColor: '#e2e2e2',
                  borderRadius: 0,
                }}
              />
              <Box
                className="concourse-tunnel-label"
                sx={{
                  position: 'absolute',
                  left: '-45px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: tunnel.label === 'Breezeway' ? '0.5rem' : '0.8rem',
                  fontWeight: 'bold',
                  color: '#f9b612',
                  textAlign: 'center',
                }}
              >
                {tunnel.label === 'West Tunnel' ? (
                  <>West<br />Tunnel</>
                ) : tunnel.label === 'East Tunnel' ? (
                  <>East<br />Tunnel</>
                ) : (
                  tunnel.label
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#F9B612', mr: 1, borderRadius: '2px' }}/>
          <Typography className="concourse-legend-label" sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
            Inbound Gate
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#FF0000', mr: 1, borderRadius: '2px' }}/>
          <Typography className="concourse-legend-label" sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
            Hot Connection (≤30 min)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#4CAF50', mr: 1, borderRadius: '2px' }}/>
          <Typography className="concourse-legend-label" sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
            Outbound Gate
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box className="concourse-tunnel" sx={{ width: 16, height: 16, backgroundColor: '#e2e2e2', mr: 1, borderRadius: '2px' }}/>
          <Typography className="concourse-legend-label" sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
            Tunnel/Breezeway
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default ConcourseMap;
